(() => {
  'use strict';

  if (window.__MYEV_DELIVERY_ESTIMATE__) {
    window.__MYEV_DELIVERY_ESTIMATE__.refresh();
    return;
  }

  const CONFIG = Object.freeze({
    timeZone: 'Europe/London',
    cutoffHour: 9,
    oneToTwoDayEndHour: 15,
    oneToTwoDayEndMinute: 46,
    eveningOrderCutoffHour: 22,
    eveningOrderCutoffMinute: 45,
    beforeCutoffDays: 1,
    afterCutoffDays: 2,
    businessDaysOnly: false,
    clockRefreshMs: 5 * 60 * 1000,
    displayRefreshMs: 30 * 1000
  });

  const ukClockFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: CONFIG.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  const weekdayFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'long'
  });
  const longMonthFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    month: 'long'
  });

  let serverOffsetMs = 0;
  let clockSource = 'device';
  let lastClockSync = 0;
  let clockSyncPromise = null;
  let renderQueued = false;

  function partsObject(formatter, date) {
    return formatter.formatToParts(date).reduce((result, part) => {
      if (part.type !== 'literal') result[part.type] = part.value;
      return result;
    }, {});
  }

  function londonNow() {
    const timestamp = Date.now() + serverOffsetMs;
    const parts = partsObject(ukClockFormatter, new Date(timestamp));
    return {
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      calendarDate: new Date(Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day)
      ))
    };
  }

  function addCalendarDays(date, numberOfDays) {
    const result = new Date(date.getTime());
    let remaining = numberOfDays;
    while (remaining > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (
        !CONFIG.businessDaysOnly
        || (result.getUTCDay() !== 0 && result.getUTCDay() !== 6)
      ) {
        remaining -= 1;
      }
    }
    return result;
  }

  function ordinal(day) {
    const lastTwoDigits = day % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) return 'th';
    if (day % 10 === 1) return 'st';
    if (day % 10 === 2) return 'nd';
    if (day % 10 === 3) return 'rd';
    return 'th';
  }

  function fullDate(date) {
    const day = date.getUTCDate();
    return `${weekdayFormatter.format(date)}, ${day}${ordinal(day)} ${longMonthFormatter.format(date)}`;
  }

  function tomorrowDate(date) {
    const day = date.getUTCDate();
    return `Tomorrow, ${day}${ordinal(day)} ${longMonthFormatter.format(date)}`;
  }

  function deliveryRange(firstDate, secondDate) {
    return `${tomorrowDate(firstDate)} / ${fullDate(secondDate)}`;
  }

  function orderCutoffText(now, weekday) {
    const minutesSinceMidnight = (now.hour * 60) + now.minute;
    const morningCutoff = CONFIG.cutoffHour * 60;
    const oneToTwoDayEnd =
      (CONFIG.oneToTwoDayEndHour * 60) + CONFIG.oneToTwoDayEndMinute;
    const eveningCutoff =
      (CONFIG.eveningOrderCutoffHour * 60) + CONFIG.eveningOrderCutoffMinute;

    if (
      weekday !== 0
      && minutesSinceMidnight >= morningCutoff
      && minutesSinceMidnight < oneToTwoDayEnd
    ) {
      return 'Order before 3:45 PM for same-day dispatch';
    }
    if (
      weekday !== 0
      && minutesSinceMidnight >= oneToTwoDayEnd
      && minutesSinceMidnight < eveningCutoff
    ) {
      return 'Order before 10:45 PM for same-day dispatch';
    }
    return 'Order before 9:00 AM';
  }

  function deliveryLabel(currentDate, deliveryDate) {
    const calendarDays = Math.round(
      (deliveryDate.getTime() - currentDate.getTime()) / 86400000
    );
    if (calendarDays === 1) return 'NEXT DAY DELIVERY';
    if (calendarDays === 2) return '2 DAY DELIVERY';
    return 'DELIVERY';
  }

  function deliveryEstimate() {
    const now = londonNow();
    const weekday = now.calendarDate.getUTCDay();
    const minutesSinceMidnight = (now.hour * 60) + now.minute;
    const beforeMorningCutoff = minutesSinceMidnight < CONFIG.cutoffHour * 60;
    const beforeAfternoonCutoff = minutesSinceMidnight < (
      (CONFIG.oneToTwoDayEndHour * 60) + CONFIG.oneToTwoDayEndMinute
    );
    const detailText = orderCutoffText(now, weekday);

    if (beforeMorningCutoff) {
      const deliveryDate = addCalendarDays(now.calendarDate, CONFIG.beforeCutoffDays);
      return {
        labelText: 'NEXT DAY DELIVERY',
        dateText: tomorrowDate(deliveryDate),
        detailText
      };
    }

    if (weekday !== 0 && beforeAfternoonCutoff) {
      const firstDeliveryDate = addCalendarDays(
        now.calendarDate,
        CONFIG.beforeCutoffDays
      );
      const secondDeliveryDate = addCalendarDays(
        now.calendarDate,
        CONFIG.afterCutoffDays
      );
      return {
        labelText: '1-2 DAY DELIVERY',
        dateText: deliveryRange(firstDeliveryDate, secondDeliveryDate),
        detailText
      };
    }

    const deliveryDate = addCalendarDays(now.calendarDate, CONFIG.afterCutoffDays);
    return {
      labelText: deliveryLabel(now.calendarDate, deliveryDate),
      dateText: fullDate(deliveryDate),
      detailText
    };
  }

  function renderElement(element, estimate) {
    const labelElement = element.querySelector('[data-myev-delivery-label]');
    const dateElement = element.querySelector('[data-myev-delivery-date]');
    const detailElement = element.querySelector('[data-myev-delivery-detail]');
    if (!labelElement || !dateElement || !detailElement) return;
    if (labelElement.textContent !== estimate.labelText) labelElement.textContent = estimate.labelText;
    if (dateElement.textContent !== estimate.dateText) dateElement.textContent = estimate.dateText;
    if (detailElement.textContent !== estimate.detailText) detailElement.textContent = estimate.detailText;
    element.dataset.myevClockSource = clockSource;
    element.classList.add('myev-delivery--ready');
  }

  function renderAll() {
    renderQueued = false;
    const estimate = deliveryEstimate();
    document.querySelectorAll('[data-myev-delivery-estimate]').forEach((element) => {
      renderElement(element, estimate);
    });
  }

  function queueRender() {
    if (renderQueued) return;
    renderQueued = true;
    window.requestAnimationFrame(renderAll);
  }

  async function syncShopifyClock(force = false) {
    const now = Date.now();
    if (!force && now - lastClockSync < CONFIG.clockRefreshMs) return;
    if (clockSyncPromise) return clockSyncPromise;

    clockSyncPromise = (async () => {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const separator = root.includes('?') ? '&' : '?';
        const response = await fetch(
          `${root}cart.js${separator}myev_delivery_clock=${now}`,
          {
            method: 'GET',
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json' }
          }
        );
        if (!response.ok) throw new Error('Shopify clock request failed');
        const serverTimestamp = Date.parse(response.headers.get('date') || '');
        if (!Number.isFinite(serverTimestamp)) throw new Error('Shopify clock header unavailable');
        if (Math.abs(serverTimestamp - Date.now()) > 24 * 60 * 60 * 1000) {
          throw new Error('Shopify clock header was outside the safe range');
        }
        serverOffsetMs = serverTimestamp - Date.now();
        clockSource = 'shopify';
        lastClockSync = Date.now();
      } catch {
        clockSource = 'device';
        lastClockSync = Date.now();
      } finally {
        clockSyncPromise = null;
        queueRender();
      }
    })();

    return clockSyncPromise;
  }

  function start() {
    renderAll();
    syncShopifyClock(true);

    const observer = new MutationObserver(queueRender);
    observer.observe(document.documentElement, { childList: true, subtree: true });

    window.setInterval(queueRender, CONFIG.displayRefreshMs);
    window.setInterval(() => syncShopifyClock(), CONFIG.clockRefreshMs);
    document.addEventListener('shopify:section:load', queueRender);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncShopifyClock(true);
    });
  }

  window.__MYEV_DELIVERY_ESTIMATE__ = {
    refresh: queueRender,
    syncClock: () => syncShopifyClock(true),
    calculate: deliveryEstimate
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
